/**
 * @format
 */
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AssessmentItem, CourseUnit, CourseUnitRealisation, OtmId } from 'common-typescript/types';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppErrorHandler, AssessmentItemEntityService, CourseUnitEntityService, sisuChain } from 'sis-components';

@Component({
    selector: 'app-course-unit-realisation-basic-info',
    templateUrl: './course-unit-realisation-basic-info.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CourseUnitRealisationBasicInfoComponent implements OnInit {
    @Input() courseUnitRealisation: CourseUnitRealisation;
    @Input() courseUnitId: OtmId;

    alternativeCourseUnits$: Observable<CourseUnit[]>;
    assessmentItemsByCourseUnitId$: Observable<Map<OtmId, AssessmentItem[]>>;

    constructor(
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly assessmentItemEntityService: AssessmentItemEntityService,
        private readonly appErrorHandler: AppErrorHandler,
    ) {}

    ngOnInit() {
        this.getAlternativeCourseUnitsAndAssessmentItems();
    }

    getAlternativeCourseUnitsAndAssessmentItems() {
        const assessmentItemIds = _.get(this.courseUnitRealisation, 'assessmentItemIds');
        this.alternativeCourseUnits$ = this.courseUnitEntityService.getByAssessmentItemIdsStudent(assessmentItemIds).pipe(
            this.appErrorHandler.defaultErrorHandler(),
            map(courseUnits => sisuChain(courseUnits).reject({ id: this.courseUnitId }).uniqBy('id').value()),
            tap(courseUnits => {
                this.assessmentItemsByCourseUnitId$ = this.assessmentItemEntityService.getByIds(assessmentItemIds).pipe(
                    this.appErrorHandler.defaultErrorHandler(),
                    map(assessmentItems => {
                        const assessmentItemsByCourseUnitId = new Map<OtmId, AssessmentItem[]>();
                        _.each(courseUnits, courseUnit => {
                            assessmentItemsByCourseUnitId.set(
                                courseUnit.id,
                                this.getCourseUnitAssessmentItems(courseUnit, assessmentItems),
                            );
                        });
                        return assessmentItemsByCourseUnitId;
                    }),
                );
            }),
        );
    }

    private getCourseUnitAssessmentItems(courseUnit: CourseUnit, assessmentItems: AssessmentItem[]) {
        const assessmentItemIds = _.flattenDeep(_.map(courseUnit.completionMethods, 'assessmentItemIds'));
        return _.filter(assessmentItems, assessmentItem => _.indexOf(assessmentItemIds, assessmentItem.id) > -1);
    }

    hasCoordinatingOrganisations() {
        return _.some(this.courseUnitRealisation.organisations, { roleUrn: 'urn:code:organisation-role:coordinating-organisation' });
    }
}
